<template>
  <div class="request-filter-header__wrapper">
    <a-row class="request-filter-header" :wrap="false">
      <input-search
        :page-name="route.name"
        placeholder="Поиск запросов..."
        :hash="currentView"
      />
      <a-badge
        :count="countFilter"
        :number-style="{
          backgroundColor: '#72c81e',
          color: '#fff',
        }"
      >
        <button-primary @click="openFilter = true"> Фильтр </button-primary>
      </a-badge>
      <RequestFilterPanel
        :is-open="openFilter"
        :filter-types="filterTypes"
        :filter-request-statuses="filterRequestStatuses"
        @update:filter="updateFilter"
        @reset-filter="resetFilter"
        @close="openFilter = false"
      />
    </a-row>
  </div>
</template>

<script setup>
  import { computed, onMounted, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import ButtonPrimary from '@/views/portal/components/UI/ButtonPrimary.vue';
  import InputSearch from '@/views/projects-showcase/components/InputSearch.vue';
  import {
    REQUEST_TYPE_EDUCATION,
    REQUEST_TYPE_EXPERTISE,
    REQUEST_TYPE_PARTNERSHIP,
    REQUEST_TYPE_RESEARCH,
    REQUEST_TYPE_RESOURCES,
  } from '@/api/models/RequestType';
  import {
    REQUEST_STATUS_NAME_DRAFT,
    REQUEST_STATUS_NAME_REVISION,
    REQUEST_STATUS_NAME_REJECTED,
    REQUEST_STATUS_NAME_MODERATOR,
    REQUEST_STATUS_NAME_ADMIN_FREE,
    REQUEST_STATUS_NAME_ADMIN_QUEUE,
    REQUEST_STATUS_NAME_LEADER,
    REQUEST_STATUS_NAME_PROJECT_START,
  } from '@/constants/request-status';
  import RequestFilterPanel from '@/components/requests/components/RequestFilterPanel.vue';

  const props = defineProps({
    /**
     * Список названий статусов проектов, которые будут показаны в фильтре статусов проектов.
     */
    statusList: {
      type: Array,
      default: () => [
        REQUEST_STATUS_NAME_DRAFT,
        REQUEST_STATUS_NAME_REVISION,
        REQUEST_STATUS_NAME_REJECTED,
        REQUEST_STATUS_NAME_MODERATOR,
        REQUEST_STATUS_NAME_ADMIN_FREE,
        REQUEST_STATUS_NAME_ADMIN_QUEUE,
        REQUEST_STATUS_NAME_LEADER,
        REQUEST_STATUS_NAME_PROJECT_START,
      ],
    },
  });

  const emit = defineEmits(['update-filter']);
  const route = useRoute();
  const currentView = computed(() => route.hash);
  const openFilter = ref(false);
  const filterRequestStatuses = ref([]);

  const filterTypes = ref([
    {
      value: REQUEST_TYPE_RESEARCH,
      label: 'Проведение исследовательских работ',
      checked: false,
    },
    { value: REQUEST_TYPE_EDUCATION, label: 'Образование', checked: false },
    {
      value: REQUEST_TYPE_EXPERTISE,
      label: 'Проведение экспертизы',
      checked: false,
    },
    {
      value: REQUEST_TYPE_PARTNERSHIP,
      label: 'Новое сотрудничество',
      checked: false,
    },
    {
      value: REQUEST_TYPE_RESOURCES,
      label: 'Использование ресурсов',
      checked: false,
    },
  ]);

  const filterRequestStatusList = [
    {
      value: REQUEST_STATUS_NAME_DRAFT,
      label: 'Черновик',
      checked: false,
    },
    {
      value: REQUEST_STATUS_NAME_REVISION,
      label: 'Доработка',
      checked: false,
    },
    {
      value: REQUEST_STATUS_NAME_REJECTED,
      label: 'Отклонено',
      checked: false,
    },
    {
      value: REQUEST_STATUS_NAME_MODERATOR,
      label: 'В обработке у модератора',
      checked: false,
    },
    {
      value: REQUEST_STATUS_NAME_ADMIN_FREE,
      label: 'Доступно администраторам',
      checked: false,
    },
    {
      value: REQUEST_STATUS_NAME_ADMIN_QUEUE,
      label: 'В обработке у администратора',
      checked: false,
    },
    {
      value: REQUEST_STATUS_NAME_LEADER,
      label: 'Назначен руководитель',
      checked: false,
    },
    {
      value: REQUEST_STATUS_NAME_PROJECT_START,
      label: 'Старт проекта',
      checked: false,
    },
  ];

  onMounted(() => {
    filterRequestStatuses.value = filterRequestStatusList.filter((el) =>
      props.statusList.includes(el.value)
    );
  });

  watch(
    () => props.statusList,
    () => {
      filterRequestStatuses.value = filterRequestStatusList.filter((el) =>
        props.statusList.includes(el.value)
      );
    }
  );

  watch(
    () => route.query.textSearch,
    async () => {
      updateFilter();
    }
  );

  const updateFilter = () => {
    const searchParams = new URLSearchParams({
      role: route.meta.role,
    });

    if (route.query.textSearch) {
      searchParams.set('textSearch', route.query.textSearch);
    }

    if (filterRequestStatuses.value.some((el) => el.checked)) {
      searchParams.append(
        'statuses',
        flattenFilterParams(filterRequestStatuses.value)
      );
    }

    if (filterTypes.value.some((el) => el.checked)) {
      searchParams.append('types', flattenFilterParams(filterTypes.value));
    }

    if (route.query.textSearch && route.query.textSearch.length > 0) {
      searchParams.append('textSearch', route.query.textSearch);
    }

    emit('update-filter', searchParams);
    openFilter.value = false;
  };

  const countFilter = computed(() => {
    const typesCount = filterTypes.value.filter((el) => el.checked).length;

    const projectStatusesCount = filterRequestStatuses.value.filter(
      (el) => el.checked
    ).length;

    return typesCount + projectStatusesCount;
  });

  const resetFilter = async () => {
    filterTypes.value.forEach((el) => (el.checked = false));
    filterRequestStatuses.value.forEach((el) => (el.checked = false));
    updateFilter();
  };

  /**
   * Преобразует массив фильтров в строку с их значениями, разделёнными запятыми.
   * Пример:
   * Входные данные:
   * [
   *   { value: 'research', label: 'Исследования', checked: true },
   *   { value: 'event', label: 'Мероприятия', checked: true },
   *   'customFilter',
   * ]
   * Результат:
   * 'research,event,customFilter'
   */
  const flattenFilterParams = (filterParams) => {
    return filterParams
      .filter((el) => typeof el === 'string' || el.checked)
      .reduce(
        (acc, el, currentIndex) =>
          acc +
          (currentIndex === 0 ? '' : ',') +
          (typeof el === 'string' ? el : el.value),
        ''
      );
  };
</script>

<style lang="scss" scoped>
  .request-filter-header__wrapper {
    background: white;
    position: sticky;
    top: -32px;
    z-index: 1;
  }

  .request-filter-header {
    background: white;
    margin: 0px -40px 0px -20px;
    align-items: center;
    border-bottom: 1px solid #f2f4f8;
    justify-content: space-between;
    gap: 20px;
    padding: 20px 40px 20px 20px;
  }
</style>
